/**
 * One thing open at a time, over the list, on every screen in the back office.
 *
 * ── WHAT WAS WRONG ──────────────────────────────────────────────────────────
 *
 * `open-row.tsx` fixed the GESTURE: the whole row opens the thing, everywhere.
 * It did not fix what opening looks like, and there were three answers.
 *
 *   Orders, Quotes, Stock       replaced the whole page with the record.
 *   Purchase orders, Documents,
 *   Product listing             expanded IN the list and pushed the rest of it
 *                               down the page.
 *   Customers                   expanded ABOVE the table, so the list jumped
 *                               and the row you clicked was nowhere near what
 *                               opened.
 *
 * The accordions are the worse of the two, because more than one can be open.
 * Christian, on the Products screen with two open: "this is a bad UI, dont you
 * think? having two open ... can we just do a side panel popup so that the list
 * is still on the background? that would be less confusing".
 *
 * Two open records is the whole problem in one sentence. The screen stops being
 * a list of products and becomes two long forms with a bit of list between
 * them, and nothing on it says which one you meant to be editing.
 *
 * Replacing the page is not much better on a queue. You open an order, press
 * the only thing that looks like a way back, and land at the top of page one
 * with your filter gone.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *
 * The list stays where it is. The record opens OVER it, on the right. Exactly
 * one is open, because the panel holds one thing and opening another replaces
 * it. Closing changes nothing about where you were.
 *
 * ── WHY AN OVERLAY AND NOT TWO COLUMNS ──────────────────────────────────────
 *
 * Two columns were built first and shown to Christian, who asked for the
 * overlay instead. He was right, and the reason is worth keeping: the sidebar
 * is 220px, so a two column layout has to fit a list and a record into what is
 * left, and on a 1380px screen the register came out squeezed. Dates wrapped,
 * the Invoice column was cut off, and the answer was either a narrower list or
 * a rule that only splits on very wide screens.
 *
 * An overlay has no such arithmetic. The list keeps its full width and the
 * panel takes what it needs, on a laptop and on an iPad alike. What it costs is
 * that the list is dimmed while the panel is open, and that is the correct
 * trade for a screen where you open one record, deal with it, and close it.
 *
 * ── THE ONE THING THIS MUST NOT DO ──────────────────────────────────────────
 *
 * It must not be closable ONLY by finding a small button. Escape closes it, and
 * so does clicking the dimmed list. A panel that traps you is worse than the
 * accordion it replaced.
 */
import { useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';

export interface DetailPanelProps {
  /** Open when this is true. The caller owns the state, usually from the URL. */
  open: boolean;
  onClose: () => void;
  /**
   * Announced when the panel opens. Name the THING, not the furniture:
   * "Order OG-29606", never "detail panel".
   */
  label: string;
  /** Drawn in the panel's header, beside the close button. Optional. */
  title?: React.ReactNode;
  children: React.ReactNode;
  /**
   * How wide, for a record that genuinely needs the room. The default suits a
   * record you read; a product with a description box and photographs wants
   * more.
   */
  width?: 'normal' | 'wide';
}

export function DetailPanel({
  open, onClose, label, title, children, width = 'normal',
}: DetailPanelProps) {
  const panel = useRef<HTMLDivElement>(null);

  /**
   * Escape closes it, from anywhere on the page.
   *
   * On `document`, not on the panel, because the person may not have clicked
   * inside it: the row they clicked is behind the scrim and still holds focus.
   */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /**
   * The page behind does not scroll while the panel is over it.
   *
   * Without this, a flick of the wheel anywhere outside the panel scrolls the
   * list underneath, so closing the panel puts the reader somewhere they never
   * navigated to.
   */
  useEffect(() => {
    if (!open) return;
    const had = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = had; };
  }, [open]);

  // Focus moves INTO the panel when it opens, or the first Tab goes to the
  // list behind it and the reader is typing into something they cannot see.
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-ink/25"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className={
          'fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-line'
          + ' bg-panel shadow-2xl outline-none'
          + (width === 'wide' ? ' sm:w-[min(94vw,860px)]' : ' sm:w-[min(94vw,560px)]')
        }
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          <div className="min-w-0 text-[13px] font-semibold">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="flex shrink-0 items-center gap-1.5 rounded-[6px] px-2.5 py-1.5 text-[13px] font-semibold text-muted hover:bg-raised hover:text-ink"
          >
            <X size={16} weight="bold" aria-hidden />
            Close
          </button>
        </div>
        {/* The panel scrolls, not the page. `min-h-0` or a long record pushes
            the header off the top instead of scrolling under it. */}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </>
  );
}
